import type {
  AssistantAnswer,
  AssistantAvailability,
  GlobalFilters,
  PortfolioNarrative,
} from '@aurora-pvi/contracts';
import { apiFetch } from './apiFetch';
import { filterParams } from './filterParams';

/**
 * The assistant, from the browser's side.
 *
 * Note what is not here: a key, a model id the client chose, and any way to
 * reach a model directly. The browser sends a question and gets back an answer
 * with its citations; every figure in it was fetched server-side, under the
 * caller's own scope, by the same routes the screens use. If this file ever
 * grows a provider SDK import, the grounding argument has been lost.
 */
export function createHttpAssistantRepository(baseUrl: string) {
  return {
    /** Whether to offer the question box at all. Discloses nothing about the estate. */
    async availability(): Promise<AssistantAvailability> {
      const response = await apiFetch(`${baseUrl}/assistant/availability`);
      if (!response.ok) {
        return {
          configured: false,
          unavailableReason: 'The platform could not be reached to ask.',
          model: null,
        };
      }
      const body = (await response.json()) as { data: AssistantAvailability };
      return body.data;
    },

    async ask(question: string): Promise<
      { ok: true; answer: AssistantAnswer } | { ok: false; message: string }
    > {
      const response = await apiFetch(`${baseUrl}/assistant/query`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ question }),
      });
      if (response.ok) {
        const body = (await response.json()) as { data: AssistantAnswer };
        return { ok: true, answer: body.data };
      }
      // The server's message explains why — not configured, too long, refused —
      // and a generic failure would throw away the only useful part.
      const body = (await response.json().catch(() => ({}))) as { message?: string };
      return { ok: false, message: body.message ?? `The question was not answered (${response.status}).` };
    },

    /** The commentary already written for this scope and period, if any. */
    async readNarrative(filters: GlobalFilters): Promise<PortfolioNarrative | null> {
      const response = await apiFetch(`${baseUrl}/assistant/narrative?${filterParams(filters)}`);
      if (!response.ok) return null;
      const body = (await response.json()) as { data: PortfolioNarrative | null };
      return body.data;
    },

    async writeNarrative(
      filters: GlobalFilters,
      redraft = false,
    ): Promise<{ ok: true; narrative: PortfolioNarrative } | { ok: false; message: string }> {
      const response = await apiFetch(`${baseUrl}/assistant/narrative?${filterParams(filters)}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ redraft }),
      });
      if (response.ok) {
        const body = (await response.json()) as { data: PortfolioNarrative };
        return { ok: true, narrative: body.data };
      }
      const body = (await response.json().catch(() => ({}))) as { message?: string };
      return { ok: false, message: body.message ?? `No commentary was produced (${response.status}).` };
    },

    async editNarrative(
      narrativeId: string,
      body: string,
    ): Promise<{ ok: true; narrative: PortfolioNarrative } | { ok: false; message: string }> {
      const response = await apiFetch(`${baseUrl}/assistant/narrative/${narrativeId}`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ body }),
      });
      if (response.ok) {
        const payload = (await response.json()) as { data: PortfolioNarrative };
        return { ok: true, narrative: payload.data };
      }
      const payload = (await response.json().catch(() => ({}))) as { message?: string };
      return { ok: false, message: payload.message ?? 'The edit was not saved.' };
    },
  };
}
